import type { EventAppModules, EventModuleKey } from '../types/eventModules.js';
import type {
  EventHomeLayout,
  EventPhase,
  HomeBlockKey,
  HomeBlockOverride,
  ResolvedHomeBlock,
  ResolvedCustomHomeSection,
} from '../types/eventHomeLayout.js';
import { EVENT_MODULE_KEYS } from '../types/eventModules.js';
import { getEventModuleLabel, isEventModuleEnabled } from './eventModules.js';

const ALL_PHASES: EventPhase[] = ['before', 'during', 'after'];
const ELIGIBLE_QUICK_ACTIONS = new Set<EventModuleKey>([
  'agenda', 'papers', 'speakers', 'exhibitors', 'sponsors', 'industry_partners', 'committee', 'attendees',
  'venue_map', 'messaging', 'notes', 'recap',
]);

type HomeCatalogEntry = Omit<ResolvedHomeBlock, 'enabled' | 'title'> & {
  title: string | ((modules: EventAppModules | undefined) => string);
};

const LEGACY_HOME_CATALOG: readonly HomeCatalogEntry[] = [
  { key: 'event_hero', title: 'Event', order: 10, visibility: ALL_PHASES, module_keys: [], limit: 1 },
  { key: 'quick_actions', title: 'Quick access', order: 20, visibility: ALL_PHASES, module_keys: ['venue_map', 'agenda', 'notes'], limit: 3 },
  { key: 'happening_now', title: 'Happening Now', order: 30, visibility: ALL_PHASES, module_keys: [], limit: 3 },
  { key: 'up_next', title: 'Up Next', order: 40, visibility: ALL_PHASES, module_keys: [], limit: 3 },
  { key: 'featured_speakers', title: (modules) => `Featured ${getEventModuleLabel(modules, 'speakers')}`, order: 50, visibility: ALL_PHASES, module_keys: [], limit: 5 },
  { key: 'recap', title: 'Conference Recap', order: 60, visibility: ALL_PHASES, module_keys: [], limit: 1 },
];

const ADAPTIVE_HOME_CATALOG: readonly HomeCatalogEntry[] = [
  { key: 'event_hero', title: 'Event', order: 10, visibility: ALL_PHASES, module_keys: [], limit: 1 },
  { key: 'recap', title: 'Conference Recap', order: 20, visibility: ['after'], module_keys: [], limit: 1 },
  { key: 'announcements', title: 'Announcements', order: 30, visibility: ALL_PHASES, module_keys: [], limit: 3 },
  { key: 'quick_actions', title: 'Quick access', order: 40, visibility: ALL_PHASES, module_keys: ['venue_map', 'agenda', 'notes'], limit: 3 },
  { key: 'happening_now', title: 'Happening Now', order: 50, visibility: ['during'], module_keys: [], limit: 3 },
  { key: 'up_next', title: 'Up Next', order: 60, visibility: ['before', 'during'], module_keys: [], limit: 3 },
  { key: 'featured_speakers', title: (modules) => `Featured ${getEventModuleLabel(modules, 'speakers')}`, order: 70, visibility: ['before', 'during'], module_keys: [], limit: 5 },
  { key: 'featured_papers', title: (modules) => `Featured ${getEventModuleLabel(modules, 'papers')}`, order: 80, visibility: ALL_PHASES, module_keys: [], limit: 3 },
  { key: 'featured_exhibitors', title: (modules) => `Featured ${getEventModuleLabel(modules, 'exhibitors')}`, order: 90, visibility: ['before', 'during'], module_keys: [], limit: 3 },
];

const BLOCK_DEPENDENCIES: Partial<Record<HomeBlockKey, EventModuleKey[]>> = {
  happening_now: ['agenda'],
  up_next: ['agenda'],
  featured_speakers: ['speakers'],
  featured_papers: ['papers'],
  featured_exhibitors: ['exhibitors'],
  recap: ['recap', 'ai_copilot'],
};

function isPhase(value: unknown): value is EventPhase {
  return value === 'before' || value === 'during' || value === 'after';
}

function normalizedVisibility(value: HomeBlockOverride['visibility'], fallback: EventPhase[]): EventPhase[] {
  if (!Array.isArray(value)) return [...fallback];
  const phases = value.filter(isPhase).filter((phase, index, all) => all.indexOf(phase) === index);
  return phases.length > 0 ? phases : [...fallback];
}

function normalizedQuickActions(value: HomeBlockOverride['module_keys'], fallback: EventModuleKey[], modules: EventAppModules | undefined): EventModuleKey[] {
  const candidates = Array.isArray(value) ? value : fallback;
  return candidates
    .filter((key): key is EventModuleKey => EVENT_MODULE_KEYS.includes(key as EventModuleKey))
    .filter((key, index, all) => all.indexOf(key) === index)
    .filter((key) => ELIGIBLE_QUICK_ACTIONS.has(key) && isEventModuleEnabled(modules, key))
    .slice(0, 3);
}

function defaultTitle(entry: HomeCatalogEntry, modules: EventAppModules | undefined): string {
  return typeof entry.title === 'function' ? entry.title(modules) : entry.title;
}

function isLegacyLayout(layout: EventHomeLayout | null | undefined): boolean {
  return !layout || Object.keys(layout).length === 0 || layout.version !== 1;
}

function isBlockAvailable(key: HomeBlockKey, modules: EventAppModules | undefined): boolean {
  return (BLOCK_DEPENDENCIES[key] ?? []).every((module) => isEventModuleEnabled(modules, module));
}

function dateInTimeZone(now: Date, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(now);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function getEventPhase(
  event: Pick<{ date_start: string; date_end: string; timezone: string }, 'date_start' | 'date_end' | 'timezone'>,
  now = new Date(),
): EventPhase {
  let today: string;
  try {
    today = dateInTimeZone(now, event.timezone);
  } catch {
    today = dateInTimeZone(now, 'UTC');
  }
  if (today < event.date_start) return 'before';
  if (today > event.date_end) return 'after';
  return 'during';
}

export function createRecommendedHomeLayout(): EventHomeLayout {
  return {
    version: 1,
    blocks: Object.fromEntries(ADAPTIVE_HOME_CATALOG.map((entry) => [entry.key, {
      enabled: true,
      order: entry.order,
      visibility: [...entry.visibility],
      ...(entry.key === 'quick_actions' ? { module_keys: [...entry.module_keys] } : {}),
      ...(entry.key.startsWith('featured_') || entry.key === 'announcements' ? { limit: entry.limit } : {}),
    }])),
  };
}

export function mergeEventHomeLayout(
  current: EventHomeLayout | null | undefined,
  edits: EventHomeLayout,
): EventHomeLayout {
  const blocks: Record<string, HomeBlockOverride> = Object.fromEntries(
    Object.entries(current?.blocks ?? {}).map(([key, value]) => [key, { ...value }]),
  );
  for (const [key, edit] of Object.entries(edits.blocks ?? {})) {
    blocks[key] = { ...blocks[key], ...edit };
  }
  return {
    version: 1,
    blocks,
    custom_sections: edits.custom_sections
      ? edits.custom_sections.map((section) => ({ ...section, visibility: [...section.visibility], action: section.action ? { ...section.action } : section.action }))
      : current?.custom_sections?.map((section) => ({ ...section, visibility: [...section.visibility], action: section.action ? { ...section.action } : section.action })),
  };
}

export function resolveCustomHomeSections(
  layout: EventHomeLayout | null | undefined,
  modules: EventAppModules | undefined,
  phase: EventPhase,
): ResolvedCustomHomeSection[] {
  return (layout?.custom_sections ?? []).flatMap((section) => {
    if (!section.enabled || !section.visibility.includes(phase)) return [];
    const action = section.action?.kind === 'internal_module'
      && !isEventModuleEnabled(modules, section.action.module_key)
      ? null
      : section.action ?? null;
    return [{
      ...section,
      title: section.title.trim(),
      body_markdown: section.body_markdown.trim(),
      visibility: [...section.visibility],
      action: action ? { ...action } : null,
    }];
  }).sort((left, right) => left.order - right.order || left.title.localeCompare(right.title));
}

export function resolveEventHomeLayout(
  layout: EventHomeLayout | null | undefined,
  modules: EventAppModules | undefined,
  phase: EventPhase,
): ResolvedHomeBlock[] {
  const legacy = isLegacyLayout(layout);
  const catalog = legacy ? LEGACY_HOME_CATALOG : ADAPTIVE_HOME_CATALOG;
  const blocks = layout?.blocks ?? {};
  const resolved = catalog.flatMap((entry) => {
    const override = blocks[entry.key];
    const enabled = typeof override?.enabled === 'boolean' ? override.enabled : true;
    const visibility = normalizedVisibility(override?.visibility, entry.visibility);
    if (!enabled || !visibility.includes(phase) || !isBlockAvailable(entry.key, modules)) return [];
    const moduleKeys = entry.key === 'quick_actions'
      ? normalizedQuickActions(override?.module_keys, entry.module_keys, modules)
      : [];
    if (entry.key === 'quick_actions' && moduleKeys.length === 0) return [];
    const title = typeof override?.title === 'string' && override.title.trim()
      ? override.title.trim()
      : defaultTitle(entry, modules);
    const limit = typeof override?.limit === 'number' && override.limit >= 1 && override.limit <= 10
      ? override.limit
      : entry.limit;
    return [{
      key: entry.key,
      enabled: true as const,
      title,
      order: typeof override?.order === 'number' ? override.order : entry.order,
      visibility,
      module_keys: moduleKeys,
      limit,
    }];
  }).sort((left, right) => left.order - right.order);

  if (resolved.length > 0) return resolved;
  const hero = catalog.find(({ key }) => key === 'event_hero') ?? ADAPTIVE_HOME_CATALOG[0];
  return [{
    key: 'event_hero',
    enabled: true,
    title: defaultTitle(hero, modules),
    order: hero.order,
    visibility: [...hero.visibility],
    module_keys: [],
    limit: 1,
  }];
}
